import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { ClipboardCheck, Check, AlertTriangle, MinusCircle, Printer, Save, Camera, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STATUSES = [
  { value: "ok", label: "OK", icon: Check, on: "bg-emerald-600 text-white border-emerald-600", off: "text-emerald-700 border-emerald-300 hover:bg-emerald-50" },
  { value: "defaut", label: "Défaut", icon: AlertTriangle, on: "bg-red-600 text-white border-red-600", off: "text-red-700 border-red-300 hover:bg-red-50" },
  { value: "na", label: "N/A", icon: MinusCircle, on: "bg-slate-500 text-white border-slate-500", off: "text-slate-600 border-slate-300 hover:bg-slate-100" },
];

const resizeImage = (file, maxSize = 1200, quality = 0.7) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
        else if (height > maxSize) { width = (width * maxSize) / height; height = maxSize; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function VehicleChecklist({ vehicle, open, onOpenChange, onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const fetchChecklist = useCallback(async () => {
    if (!vehicle?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/vehicles/${vehicle.id}/checklist`);
      setItems(res.data.items || []);
      setMeta({ updated_at: res.data.updated_at, updated_by: res.data.updated_by });
    } catch (e) {
      toast.error("Erreur de chargement de la check-list");
    }
    setLoading(false);
  }, [vehicle?.id]);

  useEffect(() => {
    if (open) fetchChecklist();
  }, [open, fetchChecklist]);

  const setStatus = (idx, status) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, status: it.status === status ? "" : status } : it));
  };
  const setNote = (idx, note) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, note } : it));
  };
  const setPhoto = async (idx, file) => {
    if (!file) return;
    try {
      const dataUrl = await resizeImage(file);
      setItems((prev) => prev.map((it, i) => i === idx ? { ...it, photo: dataUrl } : it));
    } catch (e) {
      toast.error("Impossible de charger la photo");
    }
  };
  const removePhoto = (idx) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, photo: "" } : it));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/vehicles/${vehicle.id}/checklist`, {
        km: vehicle?.kilometrage || null,
        items: items.map((it) => ({ item_id: it.item_id, label: it.label, status: it.status || "", note: it.note || "", photo: it.photo || "" })),
      });
      if (res.data.defauts > 0) {
        toast.warning(`Check-list enregistrée — ${res.data.defauts} défaut(s) signalé(s) au responsable et ajouté(s) aux "Devis à faire" (Bureau)`);
      } else {
        toast.success("Check-list enregistrée");
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    }
    setSaving(false);
  };

  const handlePrint = () => {
    const statusLabel = { ok: "✔ OK", defaut: "⚠ DÉFAUT", na: "— N/A", "": "—" };
    const rows = items.map((it) => `
      <tr>
        <td>${it.label}</td>
        <td class="st ${it.status}">${statusLabel[it.status] || "—"}</td>
        <td>${it.note || ""}${it.photo ? `<br/><img src="${it.photo}" style="max-height:120px;margin-top:6px;border:1px solid #cbd5e1;border-radius:4px"/>` : ""}</td>
      </tr>`).join("");
    const nbOk = items.filter((i) => i.status === "ok").length;
    const nbDefaut = items.filter((i) => i.status === "defaut").length;
    const nbNa = items.filter((i) => i.status === "na").length;
    const clientNom = vehicle?.client ? `${vehicle.client.nom || ""} ${vehicle.client.prenom || ""}`.trim() : "";
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>Compte rendu de contrôle</title><style>
        body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
        .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #dc2626;padding-bottom:12px;margin-bottom:16px}
        .brand{font-size:20px;font-weight:800;color:#0f172a}
        .brand span{color:#dc2626}
        h1{font-size:16px;margin:0} .meta{font-size:13px;color:#475569;line-height:1.6;margin-top:8px}
        .summary{display:flex;gap:10px;margin:12px 0}
        .chip{padding:4px 10px;border-radius:6px;font-size:13px;font-weight:700}
        .c-ok{background:#dcfce7;color:#166534}.c-def{background:#fee2e2;color:#991b1b}.c-na{background:#f1f5f9;color:#475569}
        table{width:100%;border-collapse:collapse;margin-top:8px} th,td{border:1px solid #cbd5e1;padding:8px;text-align:left;font-size:13px;vertical-align:top}
        th{background:#f1f5f9} .st.ok{color:#059669;font-weight:700} .st.defaut{color:#dc2626;font-weight:700} .st.na{color:#64748b}
        .sign{margin-top:32px;font-size:13px;color:#475569}
      </style></head><body>
        <div class="head">
          <div>
            <div class="brand">CARROSSERIE AUTO <span>GARDOISE</span></div>
            <h1 style="margin-top:8px">Compte rendu de contrôle mécanique</h1>
          </div>
          <div class="meta" style="text-align:right">
            Imprimé le ${new Date().toLocaleDateString("fr-FR")}<br/>
            ${meta.updated_by ? `Contrôle par : ${meta.updated_by}` : ""}
          </div>
        </div>
        <div class="meta">
          <strong>Véhicule :</strong> ${vehicle?.marque || ""} ${vehicle?.modele || ""} — ${vehicle?.immatriculation || ""}${vehicle?.kilometrage ? ` • ${vehicle.kilometrage.toLocaleString()} km` : ""}<br/>
          ${clientNom ? `<strong>Client :</strong> ${clientNom}<br/>` : ""}
        </div>
        <div class="summary">
          <span class="chip c-ok">${nbOk} OK</span>
          <span class="chip c-def">${nbDefaut} Défaut(s)</span>
          <span class="chip c-na">${nbNa} N/A</span>
        </div>
        <table><thead><tr><th>Point de contrôle</th><th>État</th><th>Note / Photo</th></tr></thead>
        <tbody>${rows}</tbody></table>
        <div class="sign">Signature technicien : _______________________</div>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const defautCount = items.filter((it) => it.status === "defaut").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="checklist-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" /> Check-list de contrôle mécanique
          </DialogTitle>
          <DialogDescription>
            {vehicle?.marque} {vehicle?.modele} — {vehicle?.immatriculation}
            {meta.updated_at ? ` • Dernier: ${new Date(meta.updated_at).toLocaleDateString("fr-FR")} par ${meta.updated_by || "?"}` : ""}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><div className="spinner" /></div>
        ) : (
          <div className="space-y-2 py-1">
            {items.length === 0 && (
              <p className="text-slate-500 text-center py-6">Aucun point de contrôle configuré. Configurez la liste dans Paramètres → Check-list.</p>
            )}
            {items.map((it, idx) => (
              <div key={it.item_id || idx} className="border rounded-lg p-3" data-testid={`checklist-item-${idx}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="font-medium text-slate-800">{it.label}</span>
                  <div className="flex gap-1.5">
                    {STATUSES.map((s) => {
                      const Icon = s.icon;
                      const active = it.status === s.value;
                      return (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => setStatus(idx, s.value)}
                          data-testid={`checklist-${idx}-${s.value}`}
                          className={cn("flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-semibold transition-colors", active ? s.on : s.off)}
                        >
                          <Icon className="h-3.5 w-3.5" /> {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {(it.status === "defaut" || it.note) && (
                  <Input
                    value={it.note || ""}
                    onChange={(e) => setNote(idx, e.target.value)}
                    placeholder={it.status === "defaut" ? "Décrivez le défaut..." : "Note (facultatif)"}
                    className={cn("mt-2 h-9", it.status === "defaut" && "border-red-300")}
                    data-testid={`checklist-note-${idx}`}
                  />
                )}
                {it.status === "defaut" && (
                  <div className="mt-2">
                    {it.photo ? (
                      <div className="relative inline-block group/ph">
                        <img
                          src={it.photo}
                          alt="défaut"
                          className="h-24 w-24 object-cover rounded-md border cursor-pointer"
                          onClick={() => window.open(it.photo, "_blank")}
                          data-testid={`checklist-photo-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow"
                          data-testid={`checklist-photo-remove-${idx}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <label className="inline-flex items-center gap-2 text-sm font-medium text-red-700 border border-red-300 rounded-md px-3 py-1.5 cursor-pointer hover:bg-red-50" data-testid={`checklist-photo-btn-${idx}`}>
                        <Camera className="h-4 w-4" /> Ajouter une photo
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) => setPhoto(idx, e.target.files?.[0])}
                        />
                      </label>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {defautCount > 0 && (
            <span className="text-sm text-red-600 font-medium mr-auto flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> {defautCount} défaut(s) — signalé(s) au responsable à l'enregistrement
            </span>
          )}
          <Button variant="outline" onClick={handlePrint} disabled={items.length === 0} data-testid="checklist-print-btn">
            <Printer className="h-4 w-4 mr-2" /> Imprimer
          </Button>
          <Button className="bg-red-600 hover:bg-red-700" onClick={handleSave} disabled={saving || items.length === 0} data-testid="checklist-save-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
